import styled from 'styled-components';
import { Alert } from 'antd';

import licenseType from 'models/licenseType';

import Tradeshows from './Tradeshows';
import Countries from './Countries';
import Organisations from './Organisations';
import Journals from './Journals';
import States from './States';
import FocusedResearchAreas from './FocusedResearchAreas';
import ResearchersByYear from './ResearchersByYear';
import Funding from './Funding';
import Publications from './Publications';
import { useAuth } from 'hooks';
import { ResearcherSearch } from '../helpers';
import { useSelector } from 'react-redux';
import { getSearchView } from 'selectors/search';
import OrgExhibitors from './OrgExhibitors';
import OrgVCFunding from './OrgVCFunding';
import TopInvestors from './TopInvestors';
import Regions from './Regions';
import { getResearcherText } from 'utils/constants';
import TradeshowRegionCountries from './TradeshowRegionCountries';

const VisualisationGroup = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  border-bottom: 1px solid ${p => p.theme.borderGrey};
  margin-bottom: 12px;
  padding-bottom: 4px;
`;

const StyledAlert = styled(Alert)`
  max-width: 600px;
  width: 100%;
  text-align: center;
  margin: 25px auto;
`;

const ResearcherDataView = ({
  topTradeshows,
  topTradeshowsByPosters,
  tradeshowRegions,
  tradeshowCountries,
  topCountries,
  fundingByYear,
  publicationsByYear,
  researchersByYear,
  topUsStates,
  topMeshTopics,
  regions,
  tables,
}) => {
  const { user } = useAuth();

  return (
    <>
      <SectionTitle>People Insights</SectionTitle>

      <VisualisationGroup>
        <Tradeshows
          label={`Top Global Tradeshows Where ${getResearcherText({
            capitalize: true,
            isPlural: true,
          })} Have Presented`}
          helpText={`Shows the tradeshows where ${getResearcherText({
            capitalize: false,
            isPlural: true,
          })} in your current search results presented research (including posters and presentations). Each tradeshow is counted once per ${getResearcherText(
            {
              capitalize: false,
              isPlural: false,
            }
          )}, regardless of how many presentations they gave at that event.`}
          data={topTradeshows}
          aspectRatio={4}
        />
      </VisualisationGroup>

      <VisualisationGroup>
        <FocusedResearchAreas data={topMeshTopics} fullWidth />
      </VisualisationGroup>

      <VisualisationGroup>
        <Regions
          data={regions}
          label={`${getResearcherText({
            capitalize: true,
          })} Location by Region`}
          helpText={`A bar chart displaying the top regions where matching ${getResearcherText(
            {
              isPlural: true,
            }
          )} are located`}
        />
      </VisualisationGroup>

      <VisualisationGroup>
        <Countries
          data={topCountries}
          label={`${getResearcherText({
            capitalize: true,
            isPlural: false,
          })} Location by Country`}
          helpText={`A bar chart displaying the top 10 countries where matching ${getResearcherText(
            {
              isPlural: true,
            }
          )} are located`}
        />
        <States
          data={topUsStates}
          label={`${getResearcherText({
            capitalize: true,
          })} Location by US State`}
          helpText={`A bar chart displaying the top 10 US states where matching ${getResearcherText(
            {
              isPlural: true,
            }
          )} are located`}
        />
      </VisualisationGroup>

      <SectionTitle>Activity Insights</SectionTitle>

      <VisualisationGroup>
        <Tradeshows
          data={topTradeshowsByPosters}
          label='Top Tradeshows for Matching Tradeshow Sessions'
          helpText={
            'Shows the top 20 tradeshows represented by the tradeshow sessions in your current search results. Results reflect your search criteria, including any Tradeshow Location filter. If a poster was presented at multiple conferences, each associated tradeshow is included. Multi-presenter posters are counted once.'
          }
        />
      </VisualisationGroup>

      <VisualisationGroup>
        <TradeshowRegionCountries
          data={tradeshowRegions}
          label={`Tradeshows Location by Region`}
          helpText={`A bar chart displaying the top regions where the matching tradeshows are located`}
        />
        <TradeshowRegionCountries
          data={tradeshowCountries}
          label={`Tradeshows Location by Country`}
          helpText={`A bar chart displaying the top 10 countries where the matching tradeshows are located`}
        />
      </VisualisationGroup>

      {!!user.showAdditionalGraphs && (
        <VisualisationGroup>
          <ResearchersByYear data={researchersByYear} fullWidth />
        </VisualisationGroup>
      )}

      {!!user.showAdditionalGraphs && (
        <VisualisationGroup>
          <Funding data={fundingByYear} />
          <Publications data={publicationsByYear} />
        </VisualisationGroup>
      )}

      <VisualisationGroup>
        <Organisations data={tables?.fundingByOrganisation} />
        <Journals data={tables?.topJournals} />
      </VisualisationGroup>
    </>
  );
};

const OrganisationDataView = ({
  topTradeshows,
  topExhibited,
  topVCFunding,
  topCountries,
  topUsStates,
  regions,
  tables,
}) => {
  const { hasLicense } = useAuth();
  const hasIndustryLicense = hasLicense([licenseType.industry], true);

  return (
    <>
      <SectionTitle>Organisations Insights</SectionTitle>
      <VisualisationGroup>
        <Tradeshows
          label={'Top Global Tradeshows Where Organisations Have Presented'}
          helpText={`Shows the tradeshows where organisations in your current search results presented research (including posters and presentations). Each tradeshow is counted once per organisation, regardless of how many presentations they gave at that event.`}
          data={topTradeshows}
        />
      </VisualisationGroup>
      <VisualisationGroup>
        {hasIndustryLicense && <OrgVCFunding data={topVCFunding} />}
        <OrgExhibitors data={topExhibited} fullWidth={!hasIndustryLicense} />
      </VisualisationGroup>
      <VisualisationGroup>
        <Regions
          data={regions}
          label={'Organisation Location by Region'}
          helpText={
            'A bar chart displaying the top regions where matching organisations are located'
          }
        />
      </VisualisationGroup>
      <VisualisationGroup>
        <Countries
          data={topCountries}
          label={'Organisation Location by Country'}
          helpText={
            'A bar chart displaying the top 10 countries where matching organisations are located'
          }
        />
        <States
          data={topUsStates}
          label='Organisation Location by US State'
          helpText={
            'A bar chart displaying the top 10 US states where matching organisations are located'
          }
        />
      </VisualisationGroup>

      <VisualisationGroup>
        <TopInvestors data={tables?.topInvestors} />
      </VisualisationGroup>
    </>
  );
};

const DataView = props => {
  const isResearcherView = ResearcherSearch(useSelector(getSearchView));

  return (
    <>
      {isResearcherView ? (
        <ResearcherDataView {...props} />
      ) : (
        <OrganisationDataView {...props} />
      )}
      <StyledAlert
        message='Want to see more charts and insights? Feel free to drop your suggestions into the live chat!'
        type='info'
      />
    </>
  );
};

export default DataView;
